import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Collapse
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SideBarCSS, DialogCSS } from '../styles/style';
import { SectionWrapper } from './SectionWrapper';
import { SECTION_LABELS, PDF_TEXTS, formatSectionLabel } from '../utils/texts';
import { SummaryFormat, ToneOfVoice, AiSummaryResponse } from '../types';

interface AISummaryPlaceholderProps {
  interactive?: boolean;
  isExporting?: boolean;
  enabled?: boolean;
  format?: SummaryFormat;
  tone?: ToneOfVoice;
  previewText?: string | Record<string, string> | AiSummaryResponse[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const AISummaryPlaceholder: React.FC<AISummaryPlaceholderProps> = ({
  interactive,
  isExporting,
  enabled,
  previewText,
  onRefresh,
  isRefreshing
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Actually, we want to force the summary view if previewText exists or changes
  React.useEffect(() => {
    if (previewText) {
      setShowPreview(true);
    }
  }, [previewText]);

  // If exporting and enabled, only show the green summary box if we actually have text
  // If not exporting, show based on internal toggle
  const hasText =
    typeof previewText === 'string'
      ? !!previewText
      : Array.isArray(previewText)
      ? previewText.some((item) => !!item?.response)
      : !!previewText &&
        Object.values(previewText).some((t) => !!t && typeof t === 'string');

  const shouldShowSummary = isExporting ? enabled && hasText : showPreview;

  const getSummaryContent = () => {
    if (!hasText) {
      return (
        <Typography sx={SideBarCSS.aiSummaryTextBody}>
          {PDF_TEXTS.preview.canvas.aiSummaryFallback}
        </Typography>
      );
    }

    if (typeof previewText === 'string') {
      return (
        <Typography sx={SideBarCSS.aiSummaryTextBody}>{previewText}</Typography>
      );
    }

    // Handle legacy object format
    if (!Array.isArray(previewText) && typeof previewText === 'object') {
      const entries = Object.entries(previewText).filter(
        ([k, t]) => !!k && !!t
      );
      return entries.map(([key, text], index) => {
        const label = formatSectionLabel(key);
        return (
          <Box
            key={key}
            sx={
              index === entries.length - 1
                ? {}
                : SideBarCSS.aiPreviewSectionWrapper
            }
          >
            <Typography sx={SideBarCSS.aiSummaryTextHeader}>
              {label}
              {PDF_TEXTS.aiPreview.summarySuffix}
            </Typography>
            <Typography sx={SideBarCSS.aiSummaryTextBody}>{text}</Typography>
          </Box>
        );
      });
    }

    // Handle new Array format
    if (Array.isArray(previewText)) {
      const validItems = previewText.filter((item) => !!item?.response);
      return validItems.map((item, index) => {
        const label = item.name || formatSectionLabel(item.goal);
        return (
          <Box
            key={item.goal || index}
            sx={
              index === validItems.length - 1
                ? {}
                : SideBarCSS.aiPreviewSectionWrapperLarge
            }
          >
            <Typography sx={SideBarCSS.aiSummaryTextHeader}>
              {label}
              {PDF_TEXTS.aiPreview.summarySuffix}
            </Typography>
            <Typography sx={SideBarCSS.aiSummaryTextBody}>
              {item.response}
            </Typography>
          </Box>
        );
      });
    }

    return null;
  };

  if (shouldShowSummary) {
    return (
      //<SectionWrapper interactive={interactive && !isExporting}>
      <Box sx={SideBarCSS.aiSummaryBox}>
        <Box sx={SideBarCSS.aiSummaryHeader}>
          <Box sx={SideBarCSS.aiSummaryIconWrapper}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M14.7273 6.54545L15.75 4.29545L18 3.27273L15.75 2.25L14.7273 0L13.7045 2.25L11.4545 3.27273L13.7045 4.29545L14.7273 6.54545ZM8.59091 6.95455L6.54545 2.45455L4.5 6.95455L0 9L4.5 11.0455L6.54545 15.5455L8.59091 11.0455L13.0909 9L8.59091 6.95455ZM14.7273 11.4545L13.7045 13.7045L11.4545 14.7273L13.7045 15.75L14.7273 18L15.75 15.75L18 14.7273L15.75 13.7045L14.7273 11.4545Z"
                fill="#1A927B"
              />
            </svg>
          </Box>
          <Typography sx={SideBarCSS.aiSummaryTitle}>
            {PDF_TEXTS.preview.canvas.aiSummaryTitle}
          </Typography>
          {!isExporting && hasText && (
            <IconButton
              size="small"
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                color: '#101828',
                p: 0,
                ml: 'auto', // Push to the right
                '&:hover': { backgroundColor: 'transparent', opacity: 0.7 }
              }}
            >
              {isCollapsed ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowUpIcon />
              )}
            </IconButton>
          )}
        </Box>
        <Collapse in={!isCollapsed || !!isExporting}>
          <Box sx={SideBarCSS.aiSummaryContentWrapper}>
            {getSummaryContent()}
          </Box>

          {interactive && !isExporting && (
            <Box sx={SideBarCSS.aiPlaceholderActionRow}>
              {/* Hide preview button removed at user's request to save height */}
              {hasText && (
                <Button
                  variant="outlined"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  startIcon={
                    isRefreshing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  sx={{
                    ...DialogCSS.cancelButton,
                    ...SideBarCSS.aiPlaceholderRefreshBtn
                  }}
                >
                  {isRefreshing
                    ? PDF_TEXTS.preview.canvas.refreshing
                    : PDF_TEXTS.preview.canvas.refreshSummary}
                </Button>
              )}
            </Box>
          )}
        </Collapse>
      </Box>
      //{/*</SectionWrapper>*/}
    );
  }

  // If exporting but not enabled or no text, don't show anything
  if (isExporting && (!enabled || !hasText)) return null;

  return (
    //<SectionWrapper interactive={interactive}>
    <Box sx={SideBarCSS.aiSummaryPlaceholder}>
      <Box sx={SideBarCSS.aiSummaryPlaceholderHeader}>
        <AutoAwesomeIcon sx={SideBarCSS.aiSummaryPlaceholderIcon} />
        <Typography sx={SideBarCSS.aiSummaryPlaceholderTitle}>
          {PDF_TEXTS.preview.canvas.aiSummaryPlaceholderTitle}
        </Typography>
      </Box>

      <Typography sx={SideBarCSS.aiSummaryPlaceholderText}>
        {PDF_TEXTS.preview.canvas.aiSummaryPlaceholderText}
      </Typography>

      {interactive && (
        <Button
          variant="contained"
          onClick={() => setShowPreview(true)}
          sx={SideBarCSS.aiSummaryPlaceholderButton}
        >
          {PDF_TEXTS.preview.canvas.showPreview}
        </Button>
      )}
    </Box>
    //</SectionWrapper>
  );
};
